import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Hand Steadiness Tester — accelerometer-based score over a fixed
// duration. Score = lower is better (less movement). We compute the
// trailing RMS of acceleration-minus-gravity magnitude over the test
// window.
//
// Honesty pushback applied: removed "drinking before driving" framing
// from the UI entirely. Strong medical disclaimer for tremor cases.

const DURATION_S = 10;

export default function HandSteadiness() {
  const [state, setState] = useState("idle"); // idle | ready | running | done | denied | unsupported
  const [score, setScore] = useState(null);
  const [progress, setProgress] = useState(0);
  const [history, setHistory] = useState([]); // {score, durationS}
  const handler = useRef(null);
  const samplesRef = useRef([]);
  const startTimeRef = useRef(0);
  const rafRef = useRef(null);

  const enableSensor = async () => {
    if (typeof DeviceMotionEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceMotionEvent.requestPermission === "function") {
        const r = await DeviceMotionEvent.requestPermission();
        if (r !== "granted") { setState("denied"); return; }
      }
      handler.current = (e) => {
        const a = e.accelerationIncludingGravity || e.acceleration;
        if (!a) return;
        if (state === "running") {
          const x = a.x || 0, y = a.y || 0, z = a.z || 0;
          // Compute magnitude minus gravity (so still-holding gives ~0)
          const mag = Math.sqrt(x * x + y * y + z * z);
          const lin = Math.abs(mag - 9.81);
          samplesRef.current.push(lin);
        }
      };
      window.addEventListener("devicemotion", handler.current);
      setState("ready");
    } catch {
      setState("denied");
    }
  };

  const startTest = () => {
    samplesRef.current = [];
    startTimeRef.current = performance.now();
    setProgress(0);
    setScore(null);
    setState("running");
    const tick = () => {
      const elapsed = (performance.now() - startTimeRef.current) / 1000;
      const p = Math.min(1, elapsed / DURATION_S);
      setProgress(p);
      if (p >= 1) {
        // Compute final score: mean of squared linear-acceleration values
        // (essentially mean-squared "shake"). Multiply by 100 for a
        // friendly 0-100 range.
        const samples = samplesRef.current;
        if (samples.length === 0) {
          setScore(null);
        } else {
          const meanSq = samples.reduce((s, v) => s + v * v, 0) / samples.length;
          const sc = Math.sqrt(meanSq) * 10; // RMS × 10 for readable range
          setScore(sc);
          setHistory((h) => [...h, { score: sc, when: new Date().toLocaleTimeString() }].slice(-10));
        }
        setState("done");
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // Watch state to re-bind handler reading (handler captures `state`)
  useEffect(() => () => {
    if (handler.current) window.removeEventListener("devicemotion", handler.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // Re-attach handler if state changes during a test (so capture is live)
  // Simpler: keep a ref of current "running" state
  const stateRef = useRef(state);
  stateRef.current = state;
  // Rewrite handler to use the ref
  useEffect(() => {
    if (handler.current && state === "running") {
      window.removeEventListener("devicemotion", handler.current);
      handler.current = (e) => {
        if (stateRef.current !== "running") return;
        const a = e.accelerationIncludingGravity || e.acceleration;
        if (!a) return;
        const x = a.x || 0, y = a.y || 0, z = a.z || 0;
        const mag = Math.sqrt(x * x + y * y + z * z);
        const lin = Math.abs(mag - 9.81);
        samplesRef.current.push(lin);
      };
      window.addEventListener("devicemotion", handler.current);
    }
  }, [state]);

  const interpret = (s) => {
    if (s == null) return { label: "—", color: "#9A9DBE" };
    if (s < 2) return { label: "Rock steady", color: "#5BD99A" };
    if (s < 5) return { label: "Steady", color: "#F8B84E" };
    if (s < 12) return { label: "Some movement", color: "#F5A299" };
    return { label: "Lots of movement", color: "#E0584E" };
  };
  const interp = interpret(score);

  return (
    <ToolsShell title="Hand Steadiness">
      {state === "idle" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Hold your phone as still as you can for {DURATION_S} seconds. The tool measures total movement and gives you a steadiness score. Lower is better. Tap Start to enable motion sensors.
          </p>
          <button onClick={enableSensor} style={primaryBtn}>Start</button>
          <SensorNotice state="prompt" what="Motion sensor" />
        </div>
      )}

      {state === "denied" && <SensorNotice state="denied" what="Motion sensor" />}
      {state === "unsupported" && <SensorNotice state="unsupported" what="Motion sensor" />}

      {(state === "ready" || state === "done") && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          {score !== null && (
            <div style={{ background: "#13141F", border: `1px solid ${interp.color}55`, borderRadius: 12, padding: "20px 22px", marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: "#9A9DBE" }}>Steadiness score</div>
              <div style={{ fontSize: 56, fontWeight: 800, color: interp.color, fontVariantNumeric: "tabular-nums", lineHeight: 1.05 }}>
                {score.toFixed(2)}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: interp.color, marginTop: 4 }}>{interp.label}</div>
            </div>
          )}
          <button onClick={startTest} style={primaryBtn}>
            {score !== null ? "Test again" : `Begin ${DURATION_S}-second test`}
          </button>
          {history.length > 1 && (
            <div style={{ marginTop: 18, fontSize: 12, color: "#7C8099", textAlign: "left" }}>
              Recent trials:
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {history.slice().reverse().map((h, i) => (
                  <div key={i} style={{ background: "#0F1018", padding: "4px 8px", borderRadius: 4, color: "#9A9DBE", fontVariantNumeric: "tabular-nums" }}>
                    {h.score.toFixed(2)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {state === "running" && (
        <div style={{ textAlign: "center", marginTop: 30 }}>
          <div style={{ fontSize: 24, color: "#E8E9F3", marginBottom: 20 }}>Hold still…</div>
          <div style={{ position: "relative", width: 180, height: 180, margin: "0 auto" }}>
            <svg width="180" height="180" viewBox="0 0 180 180" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="90" cy="90" r="80" fill="none" stroke="#1C1E2E" strokeWidth="10" />
              <circle cx="90" cy="90" r="80" fill="none" stroke={TOOLS_ACCENT} strokeWidth="10"
                strokeDasharray={`${80 * 2 * Math.PI * progress} ${80 * 2 * Math.PI}`}
                strokeLinecap="round" />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>
              {Math.ceil(DURATION_S - progress * DURATION_S)}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 22, padding: "14px 16px", background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 10, fontSize: 12, color: "#D9A39E" }}>
        <strong>Not a medical device.</strong> This is a casual measurement of phone motion, not a diagnostic test for tremor, neurological conditions, or any health status. If you're concerned about a tremor, see a doctor.
      </div>
    </ToolsShell>
  );
}
